import axios from "axios";
import {
  GET_TEMPLATES,
  GET_TEMPLATES_ERROR,
  TEMPLATES_LOADING,
  GET_TEMPLATE_STATS,
  UPDATE_TEMPLATE,
  DELETE_TEMPLATE,
} from "./types";
import { toastr } from "react-redux-toastr";
import { logout } from "./auth";

// Shows the backend's { errors: [{ msg }] } and logs out on an expired token
const handleError = (err, dispatch) => {
  // No response: the network or the server is down
  if (!err.response) {
    toastr.error("Сервертэй холбогдож чадсангүй");
    return;
  }
  if (
    err.response.status === 401 &&
    err.response.data.msg === "Token is not valid"
  ) {
    window.localStorage.clear("x-auth-token");
    toastr.error("Session expired");
    dispatch(logout());
  }
  const errors = err.response.data.errors;
  if (errors) {
    errors.forEach((error) => toastr.error(error.msg));
  }
};

const config = {
  headers: {
    "Content-Type": "application/json",
  },
};

//  Every template without its body
export const getTemplates = () => async (dispatch) => {
  try {
    dispatch({ type: TEMPLATES_LOADING });
    const res = await axios.get("/api/templates");
    dispatch({
      type: GET_TEMPLATES,
      payload: res.data,
    });
  } catch (err) {
    dispatch({ type: GET_TEMPLATES_ERROR });
    handleError(err, dispatch);
  }
};

//  Opened / generated / scanned per template and kiosk; days "" is all time
export const getTemplateStats = (days) => async (dispatch) => {
  try {
    const res = await axios.get("/api/templates/stats", {
      params: { days: days || undefined },
    });
    dispatch({
      type: GET_TEMPLATE_STATS,
      payload: res.data,
    });
  } catch (err) {
    handleError(err, dispatch);
  }
};

//  One template with its body for the editor, or null
export const getTemplate = (id) => async (dispatch) => {
  try {
    const res = await axios.get(`/api/templates/${id}`);
    return res.data;
  } catch (err) {
    handleError(err, dispatch);
    return null;
  }
};

//  Creates (no id) or saves a template. Returns { template } or the backend's
//  { errors }, so the editor can show them next to the form.
export const saveTemplate = (id, body) => async (dispatch) => {
  try {
    const res = id
      ? await axios.put(`/api/templates/${id}`, JSON.stringify(body), config)
      : await axios.post("/api/templates", JSON.stringify(body), config);
    dispatch({
      type: UPDATE_TEMPLATE,
      payload: res.data,
    });
    toastr.success("Хадгалагдлаа");
    return { template: res.data };
  } catch (err) {
    if (!err.response || !err.response.data.errors) handleError(err, dispatch);
    return {
      errors: (err.response && err.response.data.errors) || [],
    };
  }
};

const STATUS_MESSAGES = {
  published: "Нийтлэгдлээ: хэрэглэгчид харна",
  draft: "Ноорог боллоо: хэрэглэгчид харахгүй",
  archived: "Архивлагдлаа",
};

//  Publish, unpublish or archive. Returns the template or null.
export const setTemplateStatus = (id, status) => async (dispatch) => {
  try {
    const res = await axios.put(
      `/api/templates/${id}/status`,
      JSON.stringify({ status }),
      config
    );
    dispatch({
      type: UPDATE_TEMPLATE,
      payload: res.data,
    });
    toastr.success(STATUS_MESSAGES[status]);
    return res.data;
  } catch (err) {
    handleError(err, dispatch);
    return null;
  }
};

//  Only an unused draft can be deleted; the backend answers 409 otherwise
export const deleteTemplate = (id) => async (dispatch) => {
  try {
    await axios.delete(`/api/templates/${id}`);
    dispatch({
      type: DELETE_TEMPLATE,
      payload: id,
    });
    toastr.success("Устгагдлаа");
    return true;
  } catch (err) {
    handleError(err, dispatch);
    return false;
  }
};

//  The PDF of the unsaved body and fields as an object URL, or null.
//  The caller revokes the URL when it is replaced.
export const previewTemplate = (body) => async (dispatch) => {
  try {
    const res = await axios.post(
      "/api/templates/preview",
      JSON.stringify(body),
      { ...config, responseType: "blob" }
    );
    return URL.createObjectURL(res.data);
  } catch (err) {
    // With responseType blob the error body is a Blob too
    if (err.response && err.response.data instanceof Blob) {
      try {
        err.response.data = JSON.parse(await err.response.data.text());
      } catch (parseError) {
        err.response.data = {};
      }
    }
    handleError(err, dispatch);
    return null;
  }
};

//  The sticker QR for a kiosk ("" for none): { url, qr } or null
export const getStickerQr = (printerId) => async (dispatch) => {
  try {
    const res = await axios.get("/api/templates/sticker-qr", {
      params: { printer: printerId || undefined },
    });
    return res.data;
  } catch (err) {
    handleError(err, dispatch);
    return null;
  }
};

// ----- Themes: the looks a customer picks for a template -----

//  Every theme without its pictures, or null
export const getThemes = () => async (dispatch) => {
  try {
    const res = await axios.get("/api/template-themes");
    return res.data;
  } catch (err) {
    handleError(err, dispatch);
    return null;
  }
};

//  Adds (no slug) or saves a theme as a multipart form: its fields and, when a new
//  picture was chosen, the image and thumbnail from utils/themeImage.js. Returns
//  { theme } or the backend's { errors }.
export const saveTheme = (slug, fields, pictures) => async (dispatch) => {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => form.append(key, value));
  if (pictures) {
    form.append("image", pictures.image, "image.jpg");
    form.append("thumbnail", pictures.thumbnail, "thumbnail.jpg");
  }
  try {
    const res = slug
      ? await axios.put(`/api/template-themes/${slug}`, form)
      : await axios.post("/api/template-themes", form);
    toastr.success("Хадгалагдлаа");
    return { theme: res.data };
  } catch (err) {
    if (!err.response || !err.response.data.errors) handleError(err, dispatch);
    return {
      errors: (err.response && err.response.data.errors) || [],
    };
  }
};

//  The backend keeps a theme some template uses and answers 409
export const deleteTheme = (slug) => async (dispatch) => {
  try {
    await axios.delete(`/api/template-themes/${slug}`);
    toastr.success("Устгагдлаа");
    return true;
  } catch (err) {
    handleError(err, dispatch);
    return false;
  }
};
